import { Accordion, AccordionDetails, AccordionSummary, Autocomplete, Box, Button, Chip, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { LLM_EVENT_SOURCE } from 'src/utils/enums/llm-event-source.enum';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useEffect, useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import { withStyles } from "@mui/styles";
import { Styles } from '../../../../styles/material-styles';
import DeleteIcon from '@mui/icons-material/Delete';
import * as LLMKeywordService from "../../../../services/llmkeyword.service";
import { NotificationVariant } from "src/utils/enums/notification-variants.enum";

const EventDynamicAccordion = (props) => {
    const {
        llm,
        llmEvent,
        onShowNotification,
        onLoading,
        classes, 
        intl,
        onHandleDeleteEvent
    } = props;

    const [loadData, setLoadData] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const [keywords, setKeywords] = useState([]);

    const handleChange = async (event, isExpanded) => {
        setExpanded(isExpanded);
    };

    const fetchData = async () => {
        if (llm && llmEvent) {
            setLoadData(true);

            try {
                const response = await LLMKeywordService.getAllLLMKewWordByLlmAndLlmEvent(llm?.id, llmEvent?.id);
                if (response?.success) {
                    var data = response?.data;
                    if (data?.length > 0) {
                        let kws = data?.map(item => item?.keyword);
                        setKeywords(kws);
                    }
                } else {
                    onShowNotification("Não foi recuperar as palavras chaves.", NotificationVariant.ERROR);
                }
            } catch (err) {
                onShowNotification("Não foi recuperar as palavras chaves.", NotificationVariant.ERROR);
            } finally {
                onLoading(false);
            }
        }
    }
    
    const handleSubmit = async (event) => {
        onLoading(true);
        event.preventDefault();

        try {
            const payload = {
                llmId: llm?.id,
                llmEventId: llmEvent?.id,
                keywords
            };

            const response = await LLMKeywordService.updateByLLMAndEvent(payload);

            if (response?.success === true) {
                onShowNotification(intl.formatMessage({ id: "add.success" }));
            } else {
                onShowNotification("Não foi possível adicionar as palavras chaves.", NotificationVariant.ERROR);
            }
        } catch (err) {
            console.error(err);
            onShowNotification("Não foi possível adicionar as palavras chaves.", NotificationVariant.ERROR);
        } finally {
            onLoading(false);
        }
    }

    useEffect(() => {
        if (expanded && !loadData) {
            fetchData();
        }
    }, [expanded]);

    // Origem do evento → decide cor + icone da borda esquerda do accordion.
    // Espelha o pattern usado nas portas do LLM box no Joint (F5.5).
    const isLlmResponse = Number(llmEvent?.source) === LLM_EVENT_SOURCE.LLM_RESPONSE;
    const borderColor = isLlmResponse ? '#8e24aa' /* roxo — resposta LLM */ : '#1976d2' /* azul — msg usuario */;

    return (
        <Accordion expanded={expanded} onChange={handleChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} style={{
                borderLeft: `5px solid ${borderColor}`,
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                padding: '10px 20px',
                backgroundColor: '#f9f9f9',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                    <Typography variant="h6">{llmEvent?.name}</Typography>
                    {/* Chip de origem — icone + label + cor semantica */}
                    <Tooltip
                        arrow
                        placement="top"
                        title={
                            isLlmResponse
                                ? 'Este evento é disparado a partir da RESPOSTA gerada pela IA. Ex.: se o modelo responder algo que contenha uma palavra-chave configurada, o fluxo é redirecionado.'
                                : 'Este evento é disparado a partir da MENSAGEM enviada pelo cliente. Ex.: se o cliente digitar algo que contenha uma palavra-chave configurada, o fluxo é redirecionado antes mesmo de chamar a IA.'
                        }
                    >
                        <Chip
                            icon={isLlmResponse ? <SmartToyIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                            label={isLlmResponse ? 'Resposta da IA' : 'Mensagem do usuário'}
                            size="small"
                            sx={{
                                backgroundColor: isLlmResponse ? 'rgba(142, 36, 170, 0.12)' : 'rgba(25, 118, 210, 0.12)',
                                color: borderColor,
                                fontWeight: 600,
                                cursor: 'help',
                                '& .MuiChip-icon': { color: borderColor },
                            }}
                        />
                    </Tooltip>
                    {/* Chip auxiliar — "suprime msg" só faz sentido pra LlmResponse */}
                    {isLlmResponse && llmEvent?.suppressLlmMessage && (
                        <Tooltip title="Quando este evento dispara, a resposta gerada pela IA NÃO é entregue ao cliente.">
                            <Chip
                                icon={<VisibilityOffIcon fontSize="small" />}
                                label="Silencioso"
                                size="small"
                                variant="outlined"
                                sx={{ borderColor, color: borderColor, fontWeight: 500 }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </AccordionSummary>
            <AccordionDetails style={{ marginTop: '20px' }}>
                <div className={classes.paper} style={{ marginTop: 0 }}>
                    <form name='myForm' className={classes.form} onSubmit={handleSubmit} encType='multipart/form-data'>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <Autocomplete
                                    multiple
                                    id="tags-filled"
                                    options={[]}
                                    freeSolo
                                    value={keywords}
                                    onChange={(event, newValue, reason) => {
                                        setKeywords(newValue);
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                            const { key, ...tagProps } = getTagProps({ index });
                                            return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                                        })
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} variant="filled" label={intl.formatMessage({ id: "keywords" })} />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12} sm={12} md={12} lg={12} style={{ marginTop: 15, display: 'flex', justifyContent: 'flex-end', marginTop: 15 }}>
                                <Grid container>
                                    <Grid item xs={12} sm={12} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <IconButton 
                                            aria-label="delete" 
                                            color="error"
                                            onClick={() => onHandleDeleteEvent(llmEvent)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>

                                        <Button
                                            type='submit'
                                            variant='contained'
                                            color='primary'
                                            className={classes.submit}
                                            size="small"
                                        >
                                            {<FormattedMessage id="save" />}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>

                        </Grid>
                    </form>   
                </div>  
            </AccordionDetails>
        </Accordion>
    )
}

export default injectIntl(withStyles(Styles)(EventDynamicAccordion));